"use client";

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import useEmblaCarousel from "embla-carousel-react";

import { cn } from "@/utils/general";

export type ScienceSliderApi = {
  go: (index: number) => void;
  scrollPrev: () => void;
  scrollNext: () => void;
  selectedIndex: number;
};

type ScienceSliderProps = {
  children: React.ReactNode;
  className?: string;
  /** Applied to each Embla slide. Width/flex-basis must live here — % widths on children collapse with flex-auto. */
  slideClassName?: string;
  /** Inline styles for each Embla slide (use for dynamic flex-basis matching Splide perPage). */
  slideStyle?: React.CSSProperties;
  viewportClassName?: string;
  containerClassName?: string;
  overflow?: "hidden" | "visible";
  showArrows?: boolean;
  loop?: boolean;
  align?: "start" | "center";
  /**
   * Splide-style track padding (px). Insets snap alignment so prev/next slides peek
   * at the edges — same role as Splide `padding`.
   */
  edgePadding?: number;
  dragFree?: boolean;
  slidesToScroll?: number;
  onMove?: (index: number) => void;
  onClickSlide?: (index: number) => void;
  arrowClassName?: string;
  arrowSize?: number;
  /** hollow = translucent (default science); brand = solid #007a73 like ingredients-desktop */
  arrowVariant?: "hollow" | "brand";
};

const ArrowIcon = ({
  className,
  size = 32,
  rotate,
  variant = "hollow",
}: {
  className?: string;
  size?: number;
  rotate?: boolean;
  variant?: "hollow" | "brand";
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn(rotate && "rotate-180", className)}
  >
    {variant === "hollow" ? (
      <rect width="32" height="32" rx="2" fill="white" fillOpacity="0.2" />
    ) : null}
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5385 10.1948L17.0671 9.72339L16.1242 10.6662L16.5957 11.1376L20.7903 15.3322H9.60026H8.93359V16.6655H9.60026H20.7916L16.5957 20.8615L16.1242 21.3329L17.0671 22.2757L17.5385 21.8043L22.8718 16.4709C23.1321 16.2106 23.1321 15.7885 22.8718 15.5281L17.5385 10.1948Z"
      fill="white"
    />
  </svg>
);

export function ScienceIndicator({
  length,
  active,
  speed = 200,
}: {
  length: number;
  active: number;
  speed?: number;
}) {
  return (
    <div
      className="relative inline-flex gap-x-2 px-3 py-2 rounded-2xl bg-white/10"
      style={{ width: length * 24 - 32 }}
    >
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded bg-white transition-all ease-in-out",
            i === active ? "w-5 opacity-100" : "w-1.5 opacity-20"
          )}
          style={{ transitionDuration: `${speed}ms` }}
        />
      ))}
    </div>
  );
}

export function ScienceSlideContent({
  contents,
  active,
  className = "",
  speed = 200,
  delay = 0,
}: {
  contents: React.ReactNode[];
  active: number;
  className?: string;
  speed?: number;
  delay?: number;
}) {
  const isActive = (index: number) => {
    if (active === contents.length) return index === 0;
    if (active === -1) return index === contents.length - 1;
    return active === index;
  };

  return (
    <div className={cn("relative", className)}>
      {contents.map((content, i) => (
        <div
          key={i}
          className={cn(
            "absolute h-full w-full transition-opacity ease-in-out",
            isActive(i) ? "opacity-100" : "opacity-0"
          )}
          style={{
            transitionDuration: `${speed}ms`,
            transitionDelay: isActive(i) ? `${delay}ms` : "0ms",
          }}
        >
          {content}
        </div>
      ))}
    </div>
  );
}

export function ScienceSliderButton({
  type,
  color = "white",
  className,
  size = 32,
  fillOpacity = 0.2,
  disabled,
  onClick,
}: {
  type: "prev" | "next";
  color?: "white" | "hollow";
  className?: string;
  size?: number;
  fillOpacity?: number;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={type === "prev" ? "Previous slide" : "Next slide"}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative flex items-center justify-center pointer-events-auto transition-all duration-400 bg-nova-transparency-black-20 disabled:opacity-50 disabled:pointer-events-none",
        className
      )}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={type === "prev" ? "rotate-180" : ""}
      >
        <rect width={size} height={size} rx="2" fill={color} fillOpacity={fillOpacity} />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.5385 10.1948L17.0671 9.72339L16.1242 10.6662L16.5957 11.1376L20.7903 15.3322H9.60026H8.93359V16.6655H9.60026H20.7916L16.5957 20.8615L16.1242 21.3329L17.0671 22.2757L17.5385 21.8043L22.8718 16.4709C23.1321 16.2106 23.1321 15.7885 22.8718 15.5281L17.5385 10.1948Z"
          fill="white"
        />
      </svg>
    </button>
  );
}

const ScienceSlider = forwardRef<ScienceSliderApi, ScienceSliderProps>(function ScienceSlider(
  {
    children,
    className,
    slideClassName,
    slideStyle,
    viewportClassName,
    containerClassName,
    overflow = "hidden",
    showArrows = true,
    loop = true,
    align = "center",
    edgePadding = 0,
    dragFree = false,
    slidesToScroll = 1,
    onMove,
    onClickSlide,
    arrowClassName,
    arrowSize = 32,
    arrowVariant = "hollow",
  },
  ref
) {
  const emblaAlign = useMemo(() => {
    if (edgePadding > 0) {
      return () => edgePadding;
    }
    return align;
  }, [align, edgePadding]);

  const emblaOptions = useMemo(
    () => ({
      loop,
      align: emblaAlign,
      dragFree,
      slidesToScroll,
      containScroll: false as const,
    }),
    [loop, emblaAlign, dragFree, slidesToScroll]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const onMoveRef = useRef(onMove);
  const onClickSlideRef = useRef(onClickSlide);

  onMoveRef.current = onMove;
  onClickSlideRef.current = onClickSlide;

  /*
   * Keep select handler stable. If onMove is in the effect deps (inline lambdas from
   * parents), the effect re-runs every render and re-calls onMove(selectedScrollSnap()).
   * With mobile+desktop both mounted sharing state, the hidden carousel snaps to 0 and
   * resets the visible slider’s active index — labels/halos stick on slide 1.
   */
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      // Hidden (display:none) carousels report snap 0 — don't overwrite the visible slider's active index
      if ((emblaApi.rootNode()?.clientWidth ?? 0) === 0) return;
      onMoveRef.current?.(index);
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("settle", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("settle", onSelect);
    };
  }, [emblaApi]);

  useImperativeHandle(
    ref,
    () => ({
      go: (index: number) => emblaApi?.scrollTo(index),
      scrollPrev: () => emblaApi?.scrollPrev(),
      scrollNext: () => emblaApi?.scrollNext(),
      selectedIndex,
    }),
    [emblaApi, selectedIndex]
  );

  return (
    <div className={cn("relative cursor-grab", className)}>
      <div
        ref={emblaRef}
        className={cn(
          overflow === "visible" ? "overflow-visible" : "overflow-hidden",
          viewportClassName
        )}
      >
        <div className={cn("flex items-stretch", containerClassName)}>
          {React.Children.map(children, (child, index) => (
            <div
              className={cn(
                "min-w-0",
                !slideStyle && !slideClassName && "flex-[0_0_100%]",
                slideStyle && !slideClassName && "flex-none",
                slideClassName
              )}
              style={slideStyle}
              onClick={() => onClickSlideRef.current?.(index)}
              role={onClickSlide ? "button" : undefined}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      {showArrows && (
        <div className={cn("science-slider-arrows pointer-events-none", arrowClassName)}>
          <button
            type="button"
            aria-label="Previous"
            className={cn(
              "absolute left-2 top-1/2 z-20 -translate-y-1/2 pointer-events-auto science-slider-arrow science-slider-arrow--prev flex items-center justify-center rounded-sm",
              arrowVariant === "brand" &&
                "bg-[#007a73] transition-opacity duration-100 hover:opacity-80"
            )}
            style={{ width: arrowSize, height: arrowSize }}
            onClick={() => emblaApi?.scrollPrev()}
          >
            <ArrowIcon
              size={arrowSize}
              rotate
              variant={arrowVariant}
              className="science-slider-arrow-icon"
            />
          </button>
          <button
            type="button"
            aria-label="Next"
            className={cn(
              "absolute right-2 top-1/2 z-20 -translate-y-1/2 pointer-events-auto science-slider-arrow science-slider-arrow--next flex items-center justify-center rounded-sm",
              arrowVariant === "brand" &&
                "bg-[#007a73] transition-opacity duration-100 hover:opacity-80"
            )}
            style={{ width: arrowSize, height: arrowSize }}
            onClick={() => emblaApi?.scrollNext()}
          >
            <ArrowIcon
              size={arrowSize}
              variant={arrowVariant}
              className="science-slider-arrow-icon"
            />
          </button>
        </div>
      )}
    </div>
  );
});

export default ScienceSlider;
